/*
  Snusify: swipeable brand band.

  Concept's <marquee-element> clones its single group enough times to cover the
  viewport and runs a CSS keyframe on every group. That cannot follow a finger.
  For the band that carries the linked cans, this takes over the motion: the
  keyframe is switched off (class .snusify-swipe, see snusify-brand-home.css)
  and every group is translated by one shared offset that
    - advances on its own at Concept's configured speed and direction,
    - follows a pointer drag (mouse or touch) and a trackpad's horizontal wheel,
    - glides on release, then resumes auto-scroll after a short idle,
    - wraps by one group width, so the loop is seamless in both directions.
  A drag longer than a few pixels swallows the click so a swipe never opens a
  collection by accident. Vertical page scrolling is left to the browser via
  touch-action: pan-y.
*/
(() => {
  const SELECTOR = 'marquee-element:has(.snusify-marquee-can-link)';
  const IDLE_RESUME_MS = 1400;
  const CLICK_TOLERANCE = 6;

  const setup = (el) => {
    if (el.dataset.snusifySwipe) return;
    el.dataset.snusifySwipe = '1';

    const groups = () => [...el.children].filter((c) => c.classList.contains('marquee'));
    const groupWidth = () => (groups()[0] ? groups()[0].getBoundingClientRect().width : 0);

    const speedSetting = parseInt(el.getAttribute('data-speed') || '16', 10);
    const dir = el.getAttribute('data-direction') === 'right' ? 1 : -1;

    let offset = 0;
    let width = 0;
    let pxPerSec = 40;
    let dragging = false;
    let dragStartX = 0;
    let dragStartOffset = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0; // px per ms
    let idleUntil = 0;
    let moved = false;
    let raf = 0;
    let lastFrame = 0;

    const apply = () => {
      const w = width;
      if (w > 0) {
        // keep offset in (-2w, -w] so there is always a group to the left
        while (offset > -w) offset -= w;
        while (offset <= -2 * w) offset += w;
      }
      const t = `translate3d(${offset}px, 0, 0)`;
      for (const g of groups()) g.style.transform = t;
    };

    const measure = () => {
      width = groupWidth();
      if (!width) return;
      const parentWidth = el.getBoundingClientRect().width || 1;
      // Concept: duration for one group width = (33 - speed) * min(2.5, ceil(w / parent))
      const duration = (33 - speedSetting) * Math.min(2.5, Math.ceil(width / parentWidth));
      pxPerSec = width / Math.max(duration, 1);
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = lastFrame ? Math.min(64, now - lastFrame) : 16;
      lastFrame = now;
      if (dragging) return;

      if (Math.abs(velocity) > 0.02) {
        offset += velocity * dt;
        velocity *= Math.pow(0.94, dt / 16);
        apply();
        return;
      }
      velocity = 0;
      if (now < idleUntil) return;
      offset += (dir * pxPerSec * dt) / 1000;
      apply();
    };

    const start = () => {
      el.classList.add('snusify-swipe');
      measure();
      offset = -width; // Concept's keyframe starts one group in
      apply();
      if (!raf) raf = requestAnimationFrame(frame);
    };

    // --- pointer drag (mouse + touch). No pointer capture: capturing would
    // retarget the click to the band and the can links would never fire. The
    // move/up listeners sit on the window for the duration of a drag instead.
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - dragStartX;
      if (Math.abs(dx) > CLICK_TOLERANCE) moved = true;
      offset = dragStartOffset + dx;
      const dt = Math.max(1, e.timeStamp - lastT);
      velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = e.timeStamp;
      apply();
    };

    const release = () => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
      idleUntil = performance.now() + IDLE_RESUME_MS;
      removeEventListener('pointermove', onMove);
      removeEventListener('pointerup', release);
      removeEventListener('pointercancel', release);
    };

    el.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      dragging = true;
      moved = false;
      velocity = 0;
      dragStartX = lastX = e.clientX;
      dragStartOffset = offset;
      lastT = e.timeStamp;
      el.classList.add('is-dragging');
      addEventListener('pointermove', onMove, { passive: true });
      addEventListener('pointerup', release);
      addEventListener('pointercancel', release);
    });

    // a swipe must not open the link under the finger
    el.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
    el.addEventListener('dragstart', (e) => e.preventDefault());

    // --- trackpad / horizontal wheel
    el.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical: let the page scroll
      e.preventDefault();
      velocity = 0;
      offset -= e.deltaX;
      idleUntil = performance.now() + IDLE_RESUME_MS;
      apply();
    }, { passive: false });

    addEventListener('resize', () => { measure(); apply(); });

    // Concept clones the group when the band scrolls into view; take over then.
    // If it has not done so shortly after the band is visible (reduced-motion
    // users, or a race), clone the group here the same way Concept does, so a
    // swipe never runs into empty track.
    const cloneOurselves = () => {
      const first = groups()[0];
      if (!first || el.childElementCount > 1) return;
      const parentWidth = el.getBoundingClientRect().width || 1;
      const count = Math.ceil(parentWidth / Math.max(first.getBoundingClientRect().width, 1)) + 2;
      for (let i = 0; i < count; i++) {
        const clone = first.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('button, [href]').forEach((n) => n.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('.media').forEach((m) => m.classList.remove('loading'));
        el.appendChild(clone);
      }
    };

    if (el.childElementCount > 1) start();
    else {
      let started = false;
      const begin = () => { if (!started) { started = true; start(); } };
      const mo = new MutationObserver(() => {
        if (el.childElementCount > 1) { mo.disconnect(); begin(); }
      });
      mo.observe(el, { childList: true });
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setTimeout(() => { if (!started) { cloneOurselves(); mo.disconnect(); begin(); } }, 1200);
      }, { rootMargin: '200px 0px' });
      io.observe(el);
    }
  };

  const scan = () => document.querySelectorAll(SELECTOR).forEach(setup);
  scan();
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('page:loaded', scan);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
